import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { setNonce } from 'get-nonce';
import { ApiError } from './api/client';
import { SessionProvider } from './api/session';
import { router } from './app/router';
import { ToastProvider } from './components/toast';
import './styles.css';

// The production CSP only allows <style> elements carrying the per-request nonce. Radix's scroll lock injects one
// (via react-style-singleton, which reads it from get-nonce). In dev the placeholder is left untouched and unused.
const cspNonce = document.querySelector<HTMLMetaElement>('meta[name="csp-nonce"]')?.content;
if (cspNonce && /^[0-9a-f]{32}$/.test(cspNonce)) setNonce(cspNonce);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      refetchOnWindowFocus: true,
      // Don't retry rule violations or missing resources; do retry transient network failures.
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <SessionProvider>
          <RouterProvider router={router} />
        </SessionProvider>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
